import { useContext, useEffect, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReferenceType } from "../../../models/document";
import { ensureStaffBinderBundle } from "../../../utils/binderFeatureUtils";
import { appMutedTextStyle } from "../../../components/general/uiSurfaceStyles";
import { RouterAddress } from "../../../constants/RouterAddress";
import { v4 as uuidv4 } from "uuid";

type Props = {
  residentIds?: string[];
  staffName: string;
  staffId?: string;
  email?: string;
  onExtracted: (details: Record<string, any>) => void;
};

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = new Set(["pdf", "doc", "docx", "png", "jpg", "jpeg"]);

type ExtractionJobStatus = {
  jobId: string;
  fileName: string;
  status: string;
  errorMessage?: string;
};

const StaffCredentialDocumentUpload = ({ residentIds = [], staffName, staffId = "", email = "", onExtracted }: Props) => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [usageLimitDetails, setUsageLimitDetails] = useState<any | null>(null);
  const [extractionJobs, setExtractionJobs] = useState<ExtractionJobStatus[]>([]);
  const [jobDialogError, setJobDialogError] = useState("");
  const completedJobIds = useRef(new Set<string>());
  const normalizedStaffId = String(staffId || "").trim();
  const filesAreValid = files.length > 0
    && files.length <= MAX_FILES
    && files.every((file) => SUPPORTED_EXTENSIONS.has(file.name.split(".").pop()?.toLowerCase() || ""));
  const extractionFinished = extractionJobs.length > 0
    && extractionJobs.every((job) => ["COMPLETED", "FAILED"].includes(job.status));
  const extractionHasErrors = extractionJobs.some((job) => job.status === "FAILED");

  useEffect(() => {
    if (!extractionJobs.length || extractionFinished) return undefined;

    let cancelled = false;
    const pollJobs = async () => {
      const nextJobs = await Promise.all(extractionJobs.map(async (job) => {
        if (["COMPLETED", "FAILED"].includes(job.status)) return job;
        try {
          const result: any = await backendAPIStore.getJobDetails(job.jobId);
          const rawStatus = String(result?.jobStatus || result?.status || "PROCESSING").toUpperCase();
          const status = ["FAILED", "ERROR"].includes(rawStatus) ? "FAILED" : rawStatus === "SUCCESS" ? "COMPLETED" : rawStatus;
          if (status === "COMPLETED" && !completedJobIds.current.has(job.jobId)) {
            completedJobIds.current.add(job.jobId);
            onExtracted(result?.extractedStaffDetails || {});
          }
          return {
            ...job,
            status,
            errorMessage: status === "FAILED" ? String(result?.errorMessage || "Credential extraction failed.") : undefined,
          };
        } catch (error: any) {
          return { ...job, status: "FAILED", errorMessage: String(error?.message || "Unable to retrieve extraction status.") };
        }
      }));
      if (!cancelled) setExtractionJobs(nextJobs);
    };

    void pollJobs();
    const intervalId = window.setInterval(() => void pollJobs(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [backendAPIStore, extractionFinished, extractionJobs, onExtracted]);

  const processFiles = async () => {
    if (!files.length) return;
    if (files.some((file) => !SUPPORTED_EXTENSIONS.has(file.name.split(".").pop()?.toLowerCase() || ""))) {
      toast.current?.show({ severity: "warn", summary: "Unsupported file", detail: "Use PDF, Word, or image files.", life: 4000 });
      return;
    }
    setProcessing(true);
    try {
      await backendAPIStore.init();
      let targetStaffId = normalizedStaffId;
      if (!targetStaffId && email.trim()) {
        const account: any = await backendAPIStore.getStaffAccount(undefined, email.trim().toLowerCase());
        targetStaffId = String(account?.userId || account?.profile?.userId || account?.staff?.staffId || account?.staff?.userId || "").trim();
      }
      if (!targetStaffId) {
        throw new Error("The staff Hub account could not be found. Complete the account setup before extracting credentials.");
      }
      const allJobs: Array<{ jobId: string; fileName: string }> = [];
      const folderName = "Credentials & Training";
      const bundle = await ensureStaffBinderBundle(backendAPIStore, targetStaffId);
        const binderId = bundle.binder?.binderId;
        if (!binderId) throw new Error("Staff Binder is not available.");
        const folderCode = "credentials_training";
        let folder: any = (bundle.folders || []).find((item: any) => item.folderName === folderName || item.folderCode === folderCode);
        if (!folder) {
          folder = await backendAPIStore.createBinderFolder({ binderId, folderId: uuidv4(), folderCode, folderName, folderType: "system", shareable: true, requiredForCompliance: false, createdAt: new Date().toISOString(), createdBy: email || "staff-credentials" });
        }
        if (!folder?.folderId) throw new Error("Unable to create the Credentials & Training folder in the Staff Binder.");
        for (const file of files) {
          const document: any = await backendAPIStore.createBinderDocument({ binderId, folderId: folder.folderId, title: file.name, documentCategory: "Staff Credentials", documentNumber: "" });
          const extension = file.name.split(".").pop()?.toLowerCase() || "bin";
          const objectKey = `binder/${binderId}/${document.binderDocumentId}/${uuidv4()}.${extension}`;
          const uploadUrl = await backendAPIStore.getPresignedUrlForUpload(targetStaffId, ReferenceType.USER, objectKey);
          await backendAPIStore.uploadFileToS3(uploadUrl, file);
          await backendAPIStore.createBinderDocumentVersion({ binderDocumentId: document.binderDocumentId, fileName: file.name, s3Key: objectKey, mimeType: file.type || "application/octet-stream" });
          const job: any = await backendAPIStore.addJobDetails({ jobType: "STAFF_CREDENTIAL_EXTRACTION", referenceId: targetStaffId, staffId: targetStaffId, binderDocumentId: document.binderDocumentId, objectKey, fileName: file.name, useExistingCredit: true });
          if (job?.jobId) allJobs.push({ jobId: String(job.jobId), fileName: file.name });
        }
      if (!allJobs.length) {
        throw new Error("The credential extraction jobs could not be started.");
      }
      completedJobIds.current = new Set<string>();
      setExtractionJobs(allJobs.map((job) => ({ ...job, status: "QUEUED" })));
      setFiles([]);
      setDialogVisible(false);
    } catch (error: any) {
      const errorCode = String(error?.code || error?.cause?.message || error?.details?.code || "").trim().toUpperCase();
      if (errorCode === "SUBSCRIPTION_LIMIT_REACHED" || errorCode === "SUBSCRIPTION_REQUIRED") {
        setUsageLimitDetails({
          message: String(error?.message || error?.details?.error || "Additional access is required to extract credential details."),
          subscriptionActive: error?.details?.subscriptionActive === true,
          canBuyFeatureCredits: error?.details?.canBuyFeatureCredits !== false,
        });
      } else {
        setJobDialogError(String(error?.message || "Unable to start credential extraction."));
        setExtractionJobs([]);
      }
    } finally {
      setProcessing(false);
    }
  };

  // Credential file extraction is temporarily hidden from all staff forms.
  return null;
  /* return <div className="col-12 staff-credential-upload-host" style={{ background: "#fff7ed", border: "1px solid #fdba74", borderRadius: "12px", padding: ".75rem 1rem" }}>
    <Toast ref={toast} />
    <style>{`.staff-credential-file-upload .p-fileupload-buttonbar { padding: .4rem; gap: .35rem; } .staff-credential-file-upload .p-fileupload-content { min-height: 2.25rem; max-height: 8rem; overflow-y: auto; padding: .4rem; } .staff-credential-file-upload .p-fileupload-row { padding: .3rem 0; } section:has(.staff-credential-upload-host) { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); column-gap: 1rem; } section:has(.staff-credential-upload-host) > h3 { grid-column: 1 / -1; grid-row: 2; } section:has(.staff-credential-upload-host) > .grid { display: contents; } section:has(.staff-credential-upload-host) > .grid > * { grid-column: span 6; } section:has(.staff-credential-upload-host) > .grid > .col-12 { grid-column: 1 / -1; } section:has(.staff-credential-upload-host) > .grid > .staff-credential-upload-host { grid-column: 1 / -1; grid-row: 1; } @media (max-width: 767px) { section:has(.staff-credential-upload-host) > .grid > * { grid-column: 1 / -1; } }`}</style>
    <div className="flex flex-column align-items-center gap-2 text-center">
      <small style={appMutedTextStyle}>Use files instead of filling the form manually. Supported files: PDF, Word files, or images.</small>
      <Button type="button" label="Upload Credential File(s)" icon="pi pi-upload" severity="warning" outlined onClick={() => setDialogVisible(true)} />
    </div>
    <Dialog visible={dialogVisible} modal header="Upload Credential File(s)" style={{ width: "42rem", maxWidth: "95vw" }} contentStyle={{ maxHeight: "70vh", overflowY: "auto" }} onHide={() => !processing && setDialogVisible(false)} footer={filesAreValid ? <div className="flex justify-content-end"><Button type="button" label={processing ? "Extracting details..." : "Start Extraction"} icon="pi pi-sparkles" severity="warning" loading={processing} disabled={processing} onClick={() => void processFiles()} /></div> : null}>
      <div className="flex flex-column gap-3">
        <div style={appMutedTextStyle}>Upload up to 10 PDF, Word, or image files. The system will extract credential and training details for review.</div>
        <FileUpload className="staff-credential-file-upload" name="staffCredentialDocuments" mode="advanced" customUpload multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" maxFileSize={MAX_FILE_SIZE}
          onSelect={(event) => setFiles(((event.files || []) as File[]).slice(0, MAX_FILES))}
          onRemove={(event) => setFiles((current) => current.filter((file) => file !== event.file))}
          onClear={() => setFiles([])} uploadHandler={() => undefined}
          chooseOptions={{ label: "Choose files", icon: "pi pi-upload", className: "p-button-outlined" }} uploadOptions={{ style: { display: "none" } }} cancelOptions={{ style: { display: "none" } }} />
        {files.length > 0 ? <div style={appMutedTextStyle}>{files.length} file{files.length === 1 ? "" : "s"} selected.</div> : null}
      </div>
    </Dialog>
    <Dialog
      visible={extractionJobs.length > 0 || Boolean(jobDialogError)}
      modal
      header="Credential Extraction"
      style={{ width: "42rem", maxWidth: "95vw" }}
      closable={extractionFinished || Boolean(jobDialogError)}
      onHide={() => {
        if (extractionFinished || jobDialogError) {
          setExtractionJobs([]);
          setJobDialogError("");
        }
      }}
    >
      {jobDialogError ? (
        <div className="flex flex-column gap-3">
          <div style={{ color: "#b91c1c", fontWeight: 700 }}>Credential extraction could not be started.</div>
          <div style={{ color: "#596f80", lineHeight: 1.7 }}>{jobDialogError}</div>
          <div className="flex justify-content-end">
            <Button type="button" label="Close" severity="secondary" outlined rounded onClick={() => setJobDialogError("")} />
          </div>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <div style={{ color: "#596f80", lineHeight: 1.7 }}>
            {extractionFinished
              ? extractionHasErrors
                ? "Credential extraction finished with errors. Review the files below."
                : "Credential extraction completed successfully. The extracted details have been filled into the Staff form and are ready for review."
              : "Your files were uploaded. The system is extracting credential and training details."}
          </div>
          <div className="flex flex-column gap-2">
            {extractionJobs.map((job) => (
              <div key={job.jobId} className="flex align-items-start justify-content-between gap-3" style={{ borderBottom: "1px solid #e2e8f0", padding: "0.65rem 0" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "#0f172a", fontWeight: 600, overflowWrap: "anywhere" }}>{job.fileName}</div>
                  {job.errorMessage ? <div className="mt-1" style={{ color: "#b91c1c", fontSize: "0.9rem", lineHeight: 1.5 }}>{job.errorMessage}</div> : null}
                </div>
                <span style={{ color: job.status === "FAILED" ? "#b91c1c" : job.status === "COMPLETED" ? "#15803d" : "#9a3412", fontWeight: 700, whiteSpace: "nowrap" }}>
                  {job.status === "QUEUED" || job.status === "PROCESSING" ? "Processing" : job.status === "COMPLETED" ? "Completed" : "Failed"}
                </span>
              </div>
            ))}
          </div>
          {extractionFinished ? (
            <div className="flex justify-content-end">
              <Button type="button" label="Close" severity="secondary" outlined rounded onClick={() => setExtractionJobs([])} />
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
    <Dialog
      visible={Boolean(usageLimitDetails)}
      modal
      header="More Access Needed"
      style={{ width: "34rem", maxWidth: "95vw" }}
      onHide={() => setUsageLimitDetails(null)}
    >
      <div className="flex flex-column gap-3">
        <div style={{ color: "#596f80", lineHeight: 1.7 }}>{usageLimitDetails?.message}</div>
        <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: 14, padding: "1rem" }}>
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Next</div>
          <div className="mt-2" style={{ color: "#596f80", lineHeight: 1.7 }}>
            {usageLimitDetails?.subscriptionActive
              ? "Your monthly included usage is exhausted. Buy automation credits in Billing to continue."
              : "Subscribe to continue using credential extraction."}
          </div>
        </div>
        <div className="flex justify-content-end gap-2">
          <Button type="button" label="Close" severity="secondary" outlined rounded onClick={() => setUsageLimitDetails(null)} />
          <Button
            type="button"
            label={usageLimitDetails?.subscriptionActive ? "Buy Automation Credits" : "Subscribe"}
            severity="warning"
            rounded
            disabled={usageLimitDetails?.subscriptionActive && !usageLimitDetails?.canBuyFeatureCredits}
            onClick={() => {
              setUsageLimitDetails(null);
              navigate(`${RouterAddress.BILLING}?tab=${usageLimitDetails?.subscriptionActive ? "credits" : "subscription"}`);
            }}
          />
        </div>
      </div>
    </Dialog>
  </div>; */
};

export default StaffCredentialDocumentUpload;
